import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import {
  ActionMenu,
  Badge,
  Button,
  Div,
  Flexbox,
  InputField,
  ToggleButton,
  WrapperBox,
} from "../../Theme/GlobalStyles";
import {
  AddIcon,
  AngleLeft,
  AngleRight,
  EditIcon,
  EyeOpen,
  Location,
  Search,
  Setting,
  Users,
} from "../../Theme/Icons";
import { globalTheme } from "../../Theme/Theme";
import { Text } from "../../Theme/Typography";
import {
  addRoomDetailsToBeUpdated,
  clearCoworkSliceData,
  clearCoworkSliceStates,
  coworkData,
  getAllRooms,
  getAllTowers,
  postRoomActivity,
} from "../../../redux/cowork/coworkSlice";
import { SkeletonCard } from "../../../utils/util";
import { MODULES } from "../../../redux/appModule/appModuleSlice";
import useInfiniteScroll from "../../../utils/CustomHooks/useInfiniteScroll";
import AddTowerPopup from "../../Components/Popup/AddTowerPopup";
import AddEquipmentPopup from "../../Components/Popup/AddEquipmentPopup";
import { toastOpts } from "../../../utils/toastConfig";

const PER_PAGE = 10;
const FALLBACK_IMAGE =
  "https://filestack-mastersunion.s3.ap-south-1.amazonaws.com/w4cs9PhPSMV07oV2h5pe_WhatsApp%20Image%202025-10-11%20at%205.31.56%20PM.jpeg";

// Room image carousel styles — circular prev/next buttons over the image and
// pill-style dot indicators. Mirrors the availability (DashboardUser) card.
const carouselImg = { width: "100%", height: "100%", objectFit: "cover" };
const carouselNavBtn = (side) => ({
  position: "absolute",
  top: "50%",
  [side]: "10px",
  transform: "translateY(-50%)",
  width: "30px",
  height: "30px",
  borderRadius: "50%",
  border: "none",
  background: "rgba(255,255,255,0.85)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
  zIndex: 2,
});
const carouselDotsWrap = {
  position: "absolute",
  bottom: "12px",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  gap: "6px",
  zIndex: 2,
};
const carouselDot = (active) => ({
  width: active ? "18px" : "7px",
  height: "7px",
  borderRadius: "20px",
  background: active ? "#fff" : "rgba(255,255,255,0.6)",
  cursor: "pointer",
  transition: "all 0.2s ease",
});

// Single-image-at-a-time carousel with hover arrows + dot indicators, wrapping
// at the ends. Self-contained per-card state so it works inside a plain .map().
const RoomImageCarousel = ({ images, alt }) => {
  const roomImages = images?.length ? images : [FALLBACK_IMAGE];
  const [imgIndex, setImgIndex] = useState(0);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const hasMultipleImages = roomImages.length > 1;

  // Reset to the first image if the incoming gallery changes (card reused).
  useEffect(() => {
    setImgIndex(0);
  }, [images]);

  const goToPrevImage = (e) => {
    e?.stopPropagation();
    setImgIndex((i) => (i - 1 + roomImages.length) % roomImages.length);
  };
  const goToNextImage = (e) => {
    e?.stopPropagation();
    setImgIndex((i) => (i + 1) % roomImages.length);
  };

  return (
    <div
      style={{ position: "relative", width: "100%", height: "100%" }}
      onMouseEnter={() => setIsCardHovered(true)}
      onMouseLeave={() => setIsCardHovered(false)}
    >
      <img
        src={roomImages[imgIndex] || FALLBACK_IMAGE}
        alt={`${alt || "room"} ${imgIndex + 1}`}
        style={carouselImg}
        onError={(e) => {
          e.currentTarget.src = FALLBACK_IMAGE;
        }}
      />
      {hasMultipleImages && (
        <>
          {/* Arrows reveal on card hover; dots stay visible. */}
          {isCardHovered && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                style={carouselNavBtn("left")}
                onClick={goToPrevImage}
              >
                <AngleLeft />
              </button>
              <button
                type="button"
                aria-label="Next image"
                style={carouselNavBtn("right")}
                onClick={goToNextImage}
              >
                <AngleRight />
              </button>
            </>
          )}
          <div style={carouselDotsWrap}>
            {roomImages.map((_, i) => (
              <span
                key={i}
                style={carouselDot(i === imgIndex)}
                onClick={(e) => {
                  e.stopPropagation();
                  setImgIndex(i);
                }}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const MeetingRooms = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    isCoworkSliceSmallFetching,
    isCoworkSliceError,
    coworkSliceErrorMessage,
    AllRooms,
    AllRoomsCount,
    towersList,
  } = useSelector(coworkData);

  const {selectedModule} = useSelector((state) => state.appModule);

  const rolesCookie = Cookies.get("roles");
  const rolesArray = rolesCookie ? JSON.parse(rolesCookie) : [];
  const isClassAdmin = selectedModule;
  const isLab = selectedModule === MODULES.MAKERSLAB;
  const showSkeleton = isCoworkSliceSmallFetching && !AllRooms?.length;

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loadMoreError, setLoadMoreError] = useState(false);
  const [towerPopupOpen, setTowerPopupOpen] = useState(false);
  const [equipmentPopupOpen, setEquipmentPopupOpen] = useState(false);
  // Tracks which room's availability toggle is mid-flight (disables just that one).
  const [togglingRoomId, setTogglingRoomId] = useState(null);
  const [filters, setFilters] = useState({
    tower: "",
    capacity: "",
    availability: "",
  });

  // Quick enable/disable from the card — mirrors the old AllRooms table switch.
  // postRoomActivity flips availability server-side and updates the room in the
  // store in place, so no refetch is needed.
  const toggleAvailability = (room) => {
    setTogglingRoomId(room?.id);
    dispatch(postRoomActivity({ id: room?.id }));
  };

  // Clear the per-room toggle spinner once the small-fetch finishes.
  useEffect(() => {
    if (!isCoworkSliceSmallFetching) setTogglingRoomId(null);
  }, [isCoworkSliceSmallFetching]);

  useEffect(() => {
    dispatch(getAllTowers());
  }, [dispatch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    dispatch(clearCoworkSliceData());
    dispatch(
      getAllRooms({
        perPageCount: PER_PAGE,
        pageNumber: 1,
        searchValue: searchQuery,
        filters,
        isClassAdmin : selectedModule === MODULES.EDUSPACE,
        isLab : selectedModule === MODULES.MAKERSLAB,
        isBmb : selectedModule === MODULES.BLOOMBERG,
        organizationId: null,
      })
    );
    setLoadMoreError(false);
    return () => {
      dispatch(clearCoworkSliceStates());
      dispatch(clearCoworkSliceData());
    };
  }, [dispatch, searchQuery, filters, selectedModule]);

  useEffect(() => {
    if (isCoworkSliceError && coworkSliceErrorMessage) {
      toast.error(
        coworkSliceErrorMessage || "Couldn't load rooms. Please try again.",
        toastOpts("rooms-error")
      );
      // If we already had rooms, this error came from a "load more" page — pause
      // the infinite scroll so it stops re-firing and let the user retry.
      if (AllRooms?.length > 0) setLoadMoreError(true);
      dispatch(clearCoworkSliceStates());
    }
  }, [isCoworkSliceError, coworkSliceErrorMessage, dispatch, AllRooms?.length]);

  const loadMore = () => {
    setLoadMoreError(false);
    // Derive the next page from how many rooms are already loaded so a failed
    // page is retried (not skipped) — pages are fixed-size and appended.
    const nextPage = Math.floor((AllRooms?.length || 0) / PER_PAGE) + 1;
    dispatch(
      getAllRooms({
        perPageCount: PER_PAGE,
        pageNumber: nextPage,
        searchValue: searchQuery,
        filters,
        isClassAdmin : selectedModule === MODULES.EDUSPACE,
        isLab : selectedModule === MODULES.MAKERSLAB,
        isBmb : selectedModule === MODULES.BLOOMBERG,
        organizationId: null,
      })
    );
  };

  const hasMore = AllRooms?.length > 0 && AllRooms?.length < AllRoomsCount;
  const sentinelRef = useInfiniteScroll({
    hasMore: hasMore && !loadMoreError,
    isLoading: isCoworkSliceSmallFetching,
    loadMore,
  });

  const handleEdit = (room) => {
    dispatch(addRoomDetailsToBeUpdated(room));
    navigate("/EditMeetingRooms");
  };

  const handleAdd = () => {
    dispatch(addRoomDetailsToBeUpdated(null));
    navigate("/EditMeetingRooms");
  };

  const availabilityOptions = [
    { dropmenu: "All Statuses", value: "" },
    { dropmenu: "Available", value: "1" },
    { dropmenu: "Unavailable", value: "0" },
  ];

  const towerOptions = [
    { dropmenu: "All Locations", value: "" },
    ...(towersList || []).map((t) => ({
      dropmenu: t?.tower,
      value: t?.id ?? t?.tower,
    })),
  ];

  const capacityOptions = [
    { dropmenu: "Any Capacity", value: "" },
    { dropmenu: "2 +", value: "2" },
    { dropmenu: "3 +", value: "3" },
    { dropmenu: "4 +", value: "4" },
    { dropmenu: "5 +", value: "5" },
    { dropmenu: "6 +", value: "6" },
    { dropmenu: "7 +", value: "7" },
    { dropmenu: "8 +", value: "8" },
    { dropmenu: "9 +", value: "9" },
  ];

  const renderAmenities = (amenities) => {
    if (!amenities || amenities.length === 0) return "No amenities";
    const names = amenities.map((a) => a?.name).filter(Boolean);
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")}, +${names.length - 2}`;
  };

  return (
    <WrapperBox background={"transparent"} padding="20px" className="respPagePad">
      <AddTowerPopup isActive={towerPopupOpen} togglePopup={setTowerPopupOpen} />
      <AddEquipmentPopup
        isActive={equipmentPopupOpen}
        togglePopup={setEquipmentPopupOpen}
      />
      <Flexbox
        spaceBW
        alignCenter
        className="respToolbar"
        padding="8px"
        borderRadius={20}
        background={globalTheme.colors.white1}
      >
        <InputField
          noLabel
          hasIcon={<Search />}
          height="40px"
          placeholder="Search rooms, bookings ..."
          revIcon
          maxWidth="300px"
          minWidth="250px"
          autoFocus
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyDown={() => {}}
        />
        <Flexbox gap={12} alignCenter maxWidth="max-content" className="respToolbarGroup">
          <WrapperBox
            background={globalTheme.colors.nuetral2}
            maxWidth="max-content"
            padding={"12px"}
            borderRadius={50}
            borderColor={globalTheme.colors.nuetral2}
            maxHeight={"max-content"}
            className={"pointer"}
            overflow={"visible"}
          >
            <ActionMenu
              btnName="All Statuses"
              menuList={availabilityOptions}
              onSelect={(value) =>
                setFilters((prev) => ({ ...prev, availability: value }))
              }
            />
          </WrapperBox>
          <WrapperBox
            background={globalTheme.colors.nuetral2}
            maxWidth="max-content"
            padding={"12px"}
            borderRadius={50}
            borderColor={globalTheme.colors.nuetral2}
            maxHeight={"max-content"}
            className={"pointer"}
            overflow={"visible"}
          >
            <ActionMenu
              btnName="All Locations"
              menuList={towerOptions}
              onSelect={(value) =>
                setFilters((prev) => ({ ...prev, tower: value }))
              }
            />
          </WrapperBox>
          <WrapperBox
            background={globalTheme.colors.nuetral2}
            maxWidth="max-content"
            padding={"12px"}
            borderRadius={50}
            borderColor={globalTheme.colors.nuetral2}
            maxHeight={"max-content"}
            className={"pointer"}
            overflow={"visible"}
          >
            <ActionMenu
              btnName="Any Capacity"
              menuList={capacityOptions}
              onSelect={(value) =>
                setFilters((prev) => ({ ...prev, capacity: value }))
              }
            />
          </WrapperBox>
        </Flexbox>
        <Flexbox gap={12} alignCenter maxWidth="max-content" className="respToolbarGroup">
          <Button
            onClick={() => setTowerPopupOpen(true)}
            name="Add Tower"
            primaryOutlined
            icon={<AddIcon color={globalTheme.colors.nuetral13} />}
            revIcon
            buttonMd
          />
          {isLab && (
            <Button
              onClick={() => setEquipmentPopupOpen(true)}
              name="Add Equipment"
              primaryOutlined
              icon={<AddIcon color={globalTheme.colors.nuetral13} />}
              revIcon
              buttonMd
            />
          )}
          <Button
            onClick={handleAdd}
            name={isLab ? "New Lab" : "Add Room"}
            primaryFilled
            icon={<AddIcon />}
            revIcon
            buttonMd
          />
        </Flexbox>
      </Flexbox>

      {/* Result count — "Showing X of Y", like the old AllRooms header. */}
      <Flexbox className="mt16" alignCenter gap={4} maxWidth="max-content">
        <Text font14 inter medium black4>
          Showing {AllRooms?.length || 0}
        </Text>
        <Text font14 inter regular grey2>
          of {AllRoomsCount || 0} rooms
        </Text>
      </Flexbox>

      <Flexbox wrap alignStart className="mt20" gap={24}>
        {showSkeleton && [...Array(4)].map((_, i) => <SkeletonCard key={i} />)}
        {!showSkeleton && (AllRooms || []).map((room) => {
          const isAvailable = Number(room?.availability) === 1;
          const roomImages = room?.roomImages?.length
            ? room.roomImages
            : room?.room_thumbnail
            ? [room.room_thumbnail]
            : [FALLBACK_IMAGE];
          return (
            <WrapperBox
              key={room?.id}
              borderRadius={50}
              padding="14px 14px 20px"
              borderColor={globalTheme.colors.nuetral2}
              maxWidth={"32%"}
              overflow={"visible"}
              className="respCard3 respCardPad"
            >
              <Div
                width="100%"
                Height="300px"
                borderRadius="20"
                overflow="hidden"
              >
                <RoomImageCarousel
                  images={roomImages}
                  alt={room?.room_name || "room"}
                />
                <span
                  style={{
                    position: "absolute",
                    top: "24px",
                    right: "24px",
                    zIndex: 2,
                  }}
                >
                  <Badge
                    green={isAvailable}
                    red={!isAvailable}
                    statusText={isAvailable ? "Available" : "Under Maintenance"}
                    medium
                    borderRadius={"80px"}
                  />
                </span>
              </Div>

              <Text font16 inter medium black3 className={"mt20"}>
                {room?.room_name}
              </Text>

              <Flexbox spaceBW className="respMetaRow">
                <Flexbox
                  className="mt16 mb16"
                  gap={8}
                  alignCenter
                  maxWidth="max-content"
                >
                  <Location />
                  <Text font14 inter regular grey2>
                    {room?.tower_name || room?.tower || "—"}
                  </Text>
                </Flexbox>
                {room?.bookingsToday !== undefined && (
                  <Badge
                    statusText={`${room.bookingsToday} bookings today`}
                    primaryGrey
                    borderRadius={"20px"}
                  />
                )}
              </Flexbox>

              <Flexbox className="mb30 respMetaRow" gap={20} alignCenter>
                <Flexbox gap={8} alignCenter maxWidth="max-content">
                  <Users />
                  <Text font14 inter regular grey2>
                    {room?.capacity || 0} Seats
                  </Text>
                </Flexbox>

                <Flexbox gap={8} alignCenter maxWidth="max-content">
                  <Setting />
                  <Text font14 inter regular grey2>
                    {renderAmenities(room?.amenities)}
                  </Text>
                </Flexbox>
              </Flexbox>

              {/* Quick availability toggle — enable/disable the room without
                  opening the edit form (old AllRooms table had this switch). */}
              <Flexbox
                spaceBW
                alignCenter
                className="mb16"
                padding="10px 14px"
                borderRadius={16}
                borderColor={globalTheme.colors.nuetral2}
              >
                <Text font14 inter medium black4 maxWidth="max-content">
                  {isAvailable ? "Enabled" : "Disabled"}
                </Text>
                <ToggleButton
                  width={36}
                  height={20}
                  toggleSize={16}
                  checked={isAvailable}
                  disabled={togglingRoomId === room?.id}
                  onChange={() => toggleAvailability(room)}
                />
              </Flexbox>

              <Flexbox spaceBW alignCenter gap={12} className="respBtnRow">
                <Button
                  name="Edit"
                  icon={<EditIcon />}
                  buttonMd
                  revIcon
                  primaryOutlined
                  fullBtn
                  onClick={() => handleEdit(room)}
                />
                <Button
                  name="View Bookings"
                  icon={<EyeOpen width={"16px"} height={"16px"} />}
                  buttonMd
                  revIcon
                  primaryOutlined
                  fullBtn
                  onClick={() =>
                    navigate("/user-bookings", {
                      state: {
                        roomId: room?.id,
                        roomName: room?.room_name,
                      },
                    })
                  }
                />
              </Flexbox>
            </WrapperBox>
          );
        })}
      </Flexbox>

      {/* Infinite scroll sentinel — fetches the next page as it nears the
          viewport. Hidden while paused on error so it can't re-fire. */}
      {hasMore && !loadMoreError && (
        <div ref={sentinelRef} style={{ width: "100%", height: "1px" }} />
      )}

      {!showSkeleton && isCoworkSliceSmallFetching && AllRooms?.length > 0 && (
        <Flexbox wrap alignStart className="mt20" gap={24}>
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={`more-${i}`} />
          ))}
        </Flexbox>
      )}

      {/* Pagination failed — pause auto-loading and let the user retry. */}
      {loadMoreError && !isCoworkSliceSmallFetching && (
        <Flexbox column alignCenter gap={12} className="mt20">
          <Text font14 inter regular grey2>
            Couldn't load more rooms.
          </Text>
          <Button
            primaryOutlined
            buttonMd
            name="Retry"
            onClick={loadMore}
            isCalling={isCoworkSliceSmallFetching}
          />
        </Flexbox>
      )}
    </WrapperBox>
  );
};

export default MeetingRooms;
